import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { useAuth } from "../contexts/AuthContext";
import { API_BASE_URL } from "../config/apiconfig";
import { Upload, X, User } from "lucide-react";

const RecruiterOnbp = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [step, setStep] = useState(1);
  const [hasRow, setHasRow] = useState(false);

  // Profile fields
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [workEmail, setWorkEmail] = useState("");
  const [phone, setPhone] = useState("");
  // Country calling code states (similar to Onboarding)
  const [phoneCode, setPhoneCode] = useState("+94");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [position, setPosition] = useState("");
  const [department, setDepartment] = useState("HR");
  const [profileFile, setProfileFile] = useState(null);
  // Country code dropdown state
  const [countryOptions, setCountryOptions] = useState([]); // [{ value:'+94', label:'🇱🇰 +94 Sri Lanka', cc:'LK', country:'Sri Lanka' }]
  const [countriesLoading, setCountriesLoading] = useState(false);
  const [countriesError, setCountriesError] = useState(null);
  const [phoneDropdownOpen, setPhoneDropdownOpen] = useState(false);
  const phoneDropdownRef = useRef(null);

  // Sri Lanka hierarchical locations
  const [lkLocations, setLkLocations] = useState([]);
  const [province, setProvince] = useState("");
  const [district, setDistrict] = useState("");
  const [divisionalSecretariat, setDivisionalSecretariat] = useState("");
  const [gnd, setGnd] = useState("");
  const [village, setVillage] = useState("");
  const isSriLanka = useMemo(() => phoneCode === '+94', [phoneCode]);
  const [countryLocation, setCountryLocation] = useState("");

  // Company selection
  const [companyQuery, setCompanyQuery] = useState("");
  const [companyResults, setCompanyResults] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null); // { id, company_name }
  // Structure availability (auto-detected per company)
  const [hasCluster, setHasCluster] = useState(false);
  const [hasUnit, setHasUnit] = useState(false);
  const [clusterQuery, setClusterQuery] = useState("");
  const [clusterResults, setClusterResults] = useState([]);
  const [selectedCluster, setSelectedCluster] = useState(null); // { id, name }
  const [unitQuery, setUnitQuery] = useState("");
  const [unitResults, setUnitResults] = useState([]);
  const [selectedUnit, setSelectedUnit] = useState(null); // { id, name }
  const debouncedQuery = useMemo(() => companyQuery.trim(), [companyQuery]);

  // Validation flags
  const stepOneValid = useMemo(() => {
    return (
      !!firstName &&
      !!lastName &&
      !!workEmail &&
      !!phoneNumber &&
      !!position &&
      !!department
    );
  }, [firstName, lastName, workEmail, phoneNumber, position, department]);

  const isExistingCompany = !!selectedCompany?.id;

  const canFinish = useMemo(() => {
    // Company is required; cluster/unit optional with one constraint:
    // If company has both clusters and units, selecting a unit requires selecting a cluster too.
    if (!stepOneValid) return false;
    if (!isExistingCompany) return false;
    if (hasCluster && hasUnit && selectedUnit && !selectedCluster) return false;
    return true;
  }, [stepOneValid, isExistingCompany, hasCluster, hasUnit, selectedUnit, selectedCluster]);

  // Helper to parse existing phone string into code and number
  const parsePhone = (val) => {
    if (!val || typeof val !== 'string') return { code: "+94", number: "" };
    const m = val.trim().match(/^(\+\d{1,4})\s*(.*)$/);
    if (m) return { code: m[1], number: (m[2] || '').trim() };
    return { code: "+94", number: val.trim() };
  };

  // Keep combined phone in sync whenever code/number change
  useEffect(() => {
    const combined = `${phoneCode}${phoneNumber ? ' ' + phoneNumber : ''}`;
    setPhone(combined.trim());
  }, [phoneCode, phoneNumber]);

  useEffect(() => {
    if (!user?.id) {
      setLoading(false);
      return;
    }
    // Prefill workEmail with account email
    setWorkEmail(user.email || "");
    // Try load existing partial profile
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/recruiter/profile/${user.id}`, { cache: "no-store" });
        if (res.ok) {
          const data = await res.json();
          // If onboarding already complete, redirect away immediately
          if (data.onboarding_completed) {
            try {
              const k = user?.email ? `onboarded:${user.email}` : null;
              if (k) localStorage.setItem(k, "1");
              if (k) sessionStorage.setItem(k, String(Date.now()));
            } catch {}
            navigate('/recruiter-dashboard?onboarded=1', { replace: true });
            return;
          }
          setHasRow(!!data.id);
          setFirstName(data.first_name || "");
          setLastName(data.last_name || "");
          setWorkEmail(data.work_email || (user.email || ""));
          setPhone(data.phone || "");
          // also split into code/number for UI
          {
            const { code, number } = parsePhone(data.phone || "");
            setPhoneCode(code || "+94");
            setPhoneNumber(number || "");
          }
          setPosition(data.position || "");
          setDepartment(data.department || "HR");
          if (typeof data.onboarding_step === 'number') {
            setStep(Math.max(1, Math.min(2, data.onboarding_step + 1)));
          }
          if (data.company_id && data.company_name) {
            setSelectedCompany({ id: data.company_id, company_name: data.company_name });
          }
          // No company creation/prefill needed here anymore
          // If backend has no data, hydrate from local draft
          const emptyBackend = !data.first_name && !data.last_name && !data.phone && !data.position && !data.department && !data.work_email;
          const draftKey = `recruiterOnbDraft:${user.id}`;
          const draftRaw = localStorage.getItem(draftKey);
          if (emptyBackend && draftRaw) {
            try {
              const draft = JSON.parse(draftRaw);
              setFirstName(draft.first_name || "");
              setLastName(draft.last_name || "");
              setWorkEmail(draft.work_email || (user.email || ""));
              setPhone(draft.phone || "");
              {
                const { code, number } = parsePhone(draft.phone || "");
                setPhoneCode(code || "+94");
                setPhoneNumber(number || "");
              }
              setPosition(draft.position || "");
              setDepartment(draft.department || "HR");
              if (draft.company_name || draft.company_id) {
                setSelectedCompany({ id: draft.company_id || null, company_name: draft.company_name || "" });
              }
            } catch {}
          }
        }
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, [user]);

  // Load country calling codes from external API (once)
  useEffect(() => {
    let cancelled = false;
    const normalizeDial = (s) => {
      if (!s || typeof s !== 'string') return null;
      const match = s.match(/\+[\d-]+/g);
      if (!match || !match[0]) return null;
      const digits = match[0].replace(/[^\d]/g, '');
      if (!digits) return null;
      return `+${digits}`;
    };
    (async () => {
      try {
        setCountriesLoading(true);
        setCountriesError(null);
        const resp = await fetch('https://general.apivcm.shop/countries', { cache: 'no-store' });
        if (!resp.ok) throw new Error('Failed to load countries');
        const json = await resp.json();
        const arr = Array.isArray(json?.data) ? json.data : [];
        const mapped = arr
          .map((c) => {
            const value = normalizeDial(c?.international_dialing);
            if (!value) return null;
            const flag = c?.flag || '';
            const country = c?.country || '';
            const cc = c?.country_code || '';
            const label = `${flag ? flag + ' ' : ''}${value} ${country}`.trim();
            return { value, label, id: c?.id, cc, country };
          })
          .filter(Boolean)
          .reduce((acc, cur) => { if (!acc.some((x) => x.value === cur.value)) acc.push(cur); return acc; }, [])
          .sort((a, b) => a.value.localeCompare(b.value));
        if (!cancelled) setCountryOptions(mapped);
      } catch (e) {
        if (!cancelled) setCountriesError(e?.message || 'Failed to load');
      } finally {
        if (!cancelled) setCountriesLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // After countries load, ensure selected phoneCode is valid
  useEffect(() => {
    if (countriesLoading || countriesError || countryOptions.length === 0) return;
    const exists = countryOptions.some(o => o.value === phoneCode);
    if (!exists) {
      const lk = countryOptions.find(o => o.value === '+94');
      setPhoneCode(lk ? lk.value : countryOptions[0].value);
    }
  }, [countriesLoading, countriesError, countryOptions]);

  // Close phone dropdown on outside click or ESC
  useEffect(() => {
    if (!phoneDropdownOpen) return;
    const onDocClick = (e) => {
      if (!phoneDropdownRef.current) return;
      if (!phoneDropdownRef.current.contains(e.target)) setPhoneDropdownOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setPhoneDropdownOpen(false); };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [phoneDropdownOpen]);

  // Load Sri Lanka hierarchical data once
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch('https://general.apivcm.shop/sri-lanka-locations', { cache: 'no-store' });
        if (!r.ok) throw new Error('Failed to load Sri Lanka locations');
        const j = await r.json();
        if (!cancelled) setLkLocations(Array.isArray(j?.data) ? j.data : []);
      } catch {}
    })();
    return () => { cancelled = true; };
  }, []);

  // When phone code changes, auto-fill non-LK with country name and reset LK selections
  useEffect(() => {
    const sel = countryOptions.find(c => c.value === phoneCode);
    if (!isSriLanka) {
      setCountryLocation(sel?.country || "");
      setProvince(''); setDistrict(''); setDivisionalSecretariat(''); setGnd(''); setVillage('');
    } else {
      setCountryLocation('');
    }
  }, [phoneCode, countryOptions, isSriLanka]);

  // Derive dropdown options
  const provinceOptions = useMemo(() => lkLocations.map(p => p.province), [lkLocations]);
  const districtOptions = useMemo(() => {
    const p = lkLocations.find(x => x.province === province);
    return p ? (p.districts || []).map(d => d.district) : [];
  }, [lkLocations, province]);
  const dsOptions = useMemo(() => {
    const p = lkLocations.find(x => x.province === province);
    const d = p ? (p.districts || []).find(x => x.district === district) : null;
    return d ? (d.divisional_secretariats || []).map(ds => ds.divisional_secretariat) : [];
  }, [lkLocations, province, district]);
  const gndOptions = useMemo(() => {
    const p = lkLocations.find(x => x.province === province);
    const d = p ? (p.districts || []).find(x => x.district === district) : null;
    const ds = d ? (d.divisional_secretariats || []).find(x => x.divisional_secretariat === divisionalSecretariat) : null;
    return ds ? (ds.grama_niladhari_divisions || []).map(g => g.grama_niladhari_division) : [];
  }, [lkLocations, province, district, divisionalSecretariat]);
  const villageOptions = useMemo(() => {
    const p = lkLocations.find(x => x.province === province);
    const d = p ? (p.districts || []).find(x => x.district === district) : null;
    const ds = d ? (d.divisional_secretariats || []).find(x => x.divisional_secretariat === divisionalSecretariat) : null;
    const g = ds ? (ds.grama_niladhari_divisions || []).find(x => x.grama_niladhari_division === gnd) : null;
    return g ? (g.villages || []).map(v => v.village_name) : [];
  }, [lkLocations, province, district, divisionalSecretariat, gnd]);

  // Search companies
  useEffect(() => {
    let ac = new AbortController();
    (async () => {
      if (debouncedQuery.length < 2) {
        setCompanyResults([]);
        return;
      }
      try {
        const res = await fetch(`${API_BASE_URL}/api/companies/search?query=${encodeURIComponent(debouncedQuery)}`, {
          signal: ac.signal,
        });
        if (!res.ok) return;
        const data = await res.json();
        setCompanyResults(data || []);
      } catch {}
    })();
    return () => ac.abort();
  }, [debouncedQuery]);

  // After selecting a company, detect whether clusters/units exist
  useEffect(() => {
    (async () => {
      if (!selectedCompany?.id) { setHasCluster(false); setHasUnit(false); return; }
      try {
        const [clRes, unRes] = await Promise.all([
          fetch(`${API_BASE_URL}/api/companies/${selectedCompany.id}/clusters`),
          fetch(`${API_BASE_URL}/api/companies/${selectedCompany.id}/units`),
        ]);
        const clusters = clRes.ok ? await clRes.json() : [];
        const units = unRes.ok ? await unRes.json() : [];
        setHasCluster(Array.isArray(clusters) && clusters.length > 0);
        setHasUnit(Array.isArray(units) && units.length > 0);
        // reset selections
        setSelectedCluster(null);
        setClusterQuery("");
        setSelectedUnit(null);
        setUnitQuery("");
      } catch {
        setHasCluster(false);
        setHasUnit(false);
      }
    })();
  }, [selectedCompany?.id]);

  // Load all clusters for selected company (no search needed)
  useEffect(() => {
    let ac = new AbortController();
    (async () => {
      if (!selectedCompany?.id || !hasCluster) { setClusterResults([]); return; }
      try {
        const res = await fetch(`${API_BASE_URL}/api/companies/${selectedCompany.id}/clusters`, { signal: ac.signal });
        if (!res.ok) return;
        const data = await res.json();
        setClusterResults(Array.isArray(data) ? data : []);
      } catch {}
    })();
    return () => ac.abort();
  }, [selectedCompany?.id, hasCluster]);

  // Load all units for selected company. If cluster is selected, filter by it.
  useEffect(() => {
    let ac = new AbortController();
    (async () => {
      if (!selectedCompany?.id || !hasUnit) { setUnitResults([]); return; }
      const params = new URLSearchParams();
      if (hasCluster && selectedCluster?.id) params.set('clusterId', String(selectedCluster.id));
      const url = `${API_BASE_URL}/api/companies/${selectedCompany.id}/units${params.toString()?`?${params.toString()}`:""}`;
      try {
        const res = await fetch(url, { signal: ac.signal });
        if (!res.ok) return;
        const data = await res.json();
        setUnitResults(Array.isArray(data) ? data : []);
      } catch {}
    })();
    return () => ac.abort();
  }, [selectedCompany?.id, hasUnit, hasCluster, selectedCluster?.id]);

  // Auto-clear selected company if user edits the query to something different
  useEffect(() => {
    if (!selectedCompany) return;
    const q = companyQuery.trim();
    const sel = (selectedCompany.company_name || "").trim();
    if (q && sel && q.toLowerCase() !== sel.toLowerCase()) {
      setSelectedCompany(null);
    }
  }, [companyQuery]);

  const uploadProfilePicture = async () => {
    if (!profileFile) return null;
    const fd = new FormData();
    fd.append("profile_picture", profileFile);
    const res = await fetch(`${API_BASE_URL}/api/recruiter/profile-picture/${user.id}`, {
      method: "POST",
      body: fd,
    });
    if (!res.ok) throw new Error("Failed to upload profile picture");
    const data = await res.json();
    return data?.url || null;
  };

  const saveStepOneProgress = async () => {
    try {
      if (!hasRow) return; // Don't attempt to insert without company_id; will be created on final save
      const payload = {
        first_name: firstName,
        last_name: lastName,
        work_email: workEmail,
        phone: `${phoneCode}${phoneNumber ? ' ' + phoneNumber : ''}`,
        position,
        department,
        onboarding_step: 1,
        // location fields
        province: isSriLanka ? province : null,
        district: isSriLanka ? district : null,
        divisional_secretariat: isSriLanka ? divisionalSecretariat : null,
        grama_niladhari_division: isSriLanka ? gnd : null,
        village: isSriLanka ? village : null,
        location_country: isSriLanka ? 'Sri Lanka' : (countryLocation || null),
      };
      await fetch(`${API_BASE_URL}/api/recruiter/profile/${user.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
    } catch (e) {
      console.warn("Step 1 autosave failed", e);
    }
  };

  const handleSave = async () => {
    setError("");
    setSuccess("");

    // Basic validation
    if (!stepOneValid) {
      setError("Please complete all required fields.");
      return;
    }
    // Company selected is required; cluster/unit optional with constraint when both exist
    if (!isExistingCompany) { setError("Please select an existing company."); return; }
    if (hasCluster && hasUnit && selectedUnit && !selectedCluster) { setError("Please select a cluster for the selected unit."); return; }

    setSaving(true);
    try {
      // Save recruiter profile + link company (upsert on backend)
      const payload = {
        first_name: firstName,
        last_name: lastName,
        work_email: workEmail,
        phone: `${phoneCode}${phoneNumber ? ' ' + phoneNumber : ''}`,
        position,
        department,
        company_id: selectedCompany.id,
        cluster_id: hasCluster && selectedCluster ? selectedCluster.id : null,
        unit_id: hasUnit && selectedUnit ? selectedUnit.id : null,
        onboarding_completed: 1,
        onboarding_step: 2,
        // location fields
        province: isSriLanka ? province : null,
        district: isSriLanka ? district : null,
        divisional_secretariat: isSriLanka ? divisionalSecretariat : null,
        grama_niladhari_division: isSriLanka ? gnd : null,
        village: isSriLanka ? village : null,
        location_country: isSriLanka ? 'Sri Lanka' : (countryLocation || null),
      };
      const res = await fetch(`${API_BASE_URL}/api/recruiter/profile/${user.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error("Failed to save profile");
      const saved = await res.json();
      const resolvedCompanyId = saved?.company_id || selectedCompany?.id || null;

      // Upload profile picture after ensuring the row exists
      try {
        await uploadProfilePicture();
      } catch (e) {
        console.warn(e);
      }

      // No creation or logo upload in recruiter flow

      // Clear local draft on success
      try { localStorage.removeItem(`recruiterOnbDraft:${user.id}`); } catch {}
      setSuccess("Onboarding completed!");
      navigate('/recruiter-dashboard?onboarded=1', { replace: true });
      // Set a small grace/cache flag so guards don't bounce back
      try {
        const k = user?.email ? `onboarded:${user.email}` : null;
        if (k) localStorage.setItem(k, "1");
        if (k) sessionStorage.setItem(k, String(Date.now()));
      } catch {}

    } catch (e) {
      setError(e.message || "Failed to complete onboarding");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-gray-500">Loading...</div>
    );
  }

  const stepVariants = {
    hidden: { opacity: 0, x: 50 },
    visible: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: -50 },
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-indigo-50 to-white">
      <Header />
      <main className="flex-1 w-full max-w-5xl mx-auto p-6 md:p-12">
        <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12 space-y-8">
          <div className="text-center mb-8">
            <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-2">Welcome! Let’s set up your recruiter profile</h1>
            <p className="text-gray-500 text-lg">Step {step} of 2</p>
          </div>

          {error ? (
            <div className="p-3 rounded bg-red-50 text-red-700 border border-red-200">{error}</div>
          ) : null}
          {success ? (
            <div className="p-3 rounded bg-green-50 text-green-700 border border-green-200">{success}</div>
          ) : null}

          <AnimatePresence mode="wait">
            {step === 1 && (
              <motion.section
                key="r-step1"
                initial="hidden"
                animate="visible"
                exit="exit"
                variants={stepVariants}
                transition={{ duration: 0.5 }}
                className="space-y-6"
              >
                <h2 className="text-2xl font-semibold">Basic Details</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
                  <input
                    className="border rounded-lg p-4 text-lg"
                    placeholder="First Name"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                  />
                  <input
                    className="border rounded-lg p-4 text-lg"
                    placeholder="Last Name"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                  />
                  <input
                    className="border rounded-lg p-4 text-lg"
                    placeholder="Work Email"
                    type="email"
                    value={workEmail}
                    onChange={(e) => setWorkEmail(e.target.value)}
                  />
                  <div className="flex gap-4 relative">
                    <div className="w-40" ref={phoneDropdownRef}>
                      <button
                        type="button"
                        className="border rounded-lg p-4 text-lg w-full text-left bg-white"
                        onClick={() => setPhoneDropdownOpen((v) => !v)}
                      >
                        {countriesLoading ? 'Loading…' : phoneCode}
                      </button>
                      {phoneDropdownOpen && (
                        <div className="absolute z-20 mt-1 w-[28rem] max-w-[90vw] bg-white border rounded-lg shadow-lg max-h-72 overflow-auto">
                          <div className="sticky top-0 bg-white border-b px-3 py-2 text-xs text-gray-500">
                            <div className="grid grid-cols-[64px_96px_1fr] gap-2">
                              <span className="uppercase">CC</span>
                              <span>Code</span>
                              <span>Country</span>
                            </div>
                          </div>
                          {countriesError && (
                            <ul>
                              {['+94','+91','+971'].map((code) => (
                                <li key={code}>
                                  <button
                                    type="button"
                                    className="w-full px-3 py-2 hover:bg-indigo-50 text-left"
                                    onClick={() => { setPhoneCode(code); setPhoneDropdownOpen(false); }}
                                  >
                                    <div className="grid grid-cols-[64px_96px_1fr] gap-2">
                                      <span className="text-gray-500 font-mono">--</span>
                                      <span className="font-medium font-mono">{code}</span>
                                      <span className="text-gray-700">&nbsp;</span>
                                    </div>
                                  </button>
                                </li>
                              ))}
                            </ul>
                          )}
                          {!countriesError && (
                            <ul>
                              {countryOptions.map((o) => (
                                <li key={`${o.cc}-${o.value}`}>
                                  <button
                                    type="button"
                                    className="w-full px-3 py-2 hover:bg-indigo-50 text-left"
                                    onClick={() => { setPhoneCode(o.value); setPhoneDropdownOpen(false); }}
                                  >
                                    <div className="grid grid-cols-[64px_96px_1fr] gap-2">
                                      <span className="uppercase text-gray-500 font-mono">{o.cc || '--'}</span>
                                      <span className="font-medium font-mono">{o.value}</span>
                                      <span className="text-gray-700 truncate">{o.country}</span>
                                    </div>
                                  </button>
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      )}
                    </div>
                    <input
                      className="border rounded-lg p-4 text-lg flex-1"
                      placeholder="Mobile Number"
                      value={phoneNumber}
                      onChange={(e) => setPhoneNumber(e.target.value)}
                    />
                  </div>
                  {isSriLanka ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:col-span-2">
                      <div>
                        <label className="block text-sm text-gray-700 mb-1">Province</label>
                        <select className="border rounded-lg p-3 w-full" value={province} onChange={(e)=>{ setProvince(e.target.value); setDistrict(''); setDivisionalSecretariat(''); setGnd(''); setVillage(''); }}>
                          <option value="">Select province</option>
                          {provinceOptions.map(p => (<option key={p} value={p}>{p}</option>))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm text-gray-700 mb-1">District</label>
                        <select className="border rounded-lg p-3 w-full" value={district} onChange={(e)=>{ setDistrict(e.target.value); setDivisionalSecretariat(''); setGnd(''); setVillage(''); }} disabled={!province}>
                          <option value="">Select district</option>
                          {districtOptions.map(d => (<option key={d} value={d}>{d}</option>))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm text-gray-700 mb-1">Divisional Secretariat</label>
                        <select className="border rounded-lg p-3 w-full" value={divisionalSecretariat} onChange={(e)=>{ setDivisionalSecretariat(e.target.value); setGnd(''); setVillage(''); }} disabled={!district}>
                          <option value="">Select divisional secretariat</option>
                          {dsOptions.map(ds => (<option key={ds} value={ds}>{ds}</option>))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm text-gray-700 mb-1">Grama Niladhari Division</label>
                        <select className="border rounded-lg p-3 w-full" value={gnd} onChange={(e)=>{ setGnd(e.target.value); setVillage(''); }} disabled={!divisionalSecretariat}>
                          <option value="">Select GND</option>
                          {gndOptions.map(g => (<option key={g} value={g}>{g}</option>))}
                        </select>
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-sm text-gray-700 mb-1">Village</label>
                        <select className="border rounded-lg p-3 w-full" value={village} onChange={(e)=> setVillage(e.target.value)} disabled={!gnd}>
                          <option value="">Select village</option>
                          {villageOptions.map(v => (<option key={v} value={v}>{v}</option>))}
                        </select>
                      </div>
                    </div>
                  ) : (
                    <div className="md:col-span-2">
                      <label className="block text-sm text-gray-700 mb-1">Location</label>
                      <input className="border rounded-lg p-4 text-lg w-full" value={countryLocation} readOnly placeholder="Country will auto-fill" />
                    </div>
                  )}
                  <div>
                    <label className="block text-sm text-gray-700 mb-1">Position (HR)</label>
                    <select
                      className="border rounded-lg p-4 text-lg w-full"
                      value={position}
                      onChange={(e) => setPosition(e.target.value)}
                    >
                      <option value="">Select position</option>
                      <option value="HR Assistant">HR Assistant</option>
                      <option value="HR Executive">HR Executive</option>
                      <option value="HR Officer">HR Officer</option>
                      <option value="Senior HR Executive">Senior HR Executive</option>
                      <option value="HR Coordinator">HR Coordinator</option>
                      <option value="HR Business Partner">HR Business Partner</option>
                      <option value="Talent Acquisition Specialist">Talent Acquisition Specialist</option>
                      <option value="Recruiter">Recruiter</option>
                      <option value="Senior Recruiter">Senior Recruiter</option>
                      <option value="Training and Development Officer">Training and Development Officer</option>
                      <option value="Compensation and Benefits Specialist">Compensation and Benefits Specialist</option>
                      <option value="HR Manager">HR Manager</option>
                      <option value="Senior HR Manager">Senior HR Manager</option>
                      <option value="Head of HR">Head of HR</option>
                      <option value="HR Director">HR Director</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-700 mb-1">Department</label>
                    <input
                      className="border rounded-lg p-4 text-lg w-full bg-gray-100 text-gray-700"
                      value={department || 'HR'}
                      readOnly
                    />
                  </div>
                  <div className="md:col-span-2 flex flex-col items-center gap-4">
                    {profileFile ? (
                      <div className="relative">
                        <img
                          src={URL.createObjectURL(profileFile)}
                          alt="Profile Preview"
                          className="w-24 h-24 rounded-full object-cover border-2 border-indigo-100"
                        />
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); setProfileFile(null); }}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
                          aria-label="Remove picture"
                        >
                          <X size={16} />
                        </button>
                      </div>
                    ) : (
                      <div className="w-24 h-24 rounded-full bg-gray-100 flex items-center justify-center text-gray-400">
                        <User size={32} />
                      </div>
                    )}
                    <label className="border rounded-lg p-4 flex items-center justify-between cursor-pointer w-full">
                      <span className="text-gray-600 text-lg">{profileFile ? 'Change Picture' : 'Upload Profile Picture'}</span>
                      <div className="flex items-center gap-2">
                        <Upload size={22} />
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) setProfileFile(file);
                          }}
                        />
                      </div>
                    </label>
                  </div>
                </div>

                <div className="flex justify-between mt-6">
                  <button
                    className="px-6 py-3 rounded-lg border text-lg font-medium hover:bg-gray-100 transition"
                    onClick={()=>navigate('/login')}
                  >
                    Cancel
                  </button>
                  <button
                    className="px-6 py-3 rounded-lg bg-indigo-600 text-white text-lg font-medium hover:bg-indigo-700 transition disabled:opacity-50"
                    disabled={!stepOneValid}
                    onClick={async()=>{
                      // Save draft locally for persistence across logout/login
                      const draftKey = `recruiterOnbDraft:${user.id}`;
                      localStorage.setItem(draftKey, JSON.stringify({
                        first_name: firstName,
                        last_name: lastName,
                        work_email: workEmail,
                        phone: `${phoneCode}${phoneNumber ? ' ' + phoneNumber : ''}`,
                        position,
                        department,
                        company_id: selectedCompany?.id || null,
                        company_name: selectedCompany?.company_name || debouncedQuery || "",
                      }));
                      if (!stepOneValid) return;
                      await saveStepOneProgress();
                      setStep(2);
                    }}
                  >
                    Next
                  </button>
                </div>
              </motion.section>
            )}

            {step === 2 && (
              <motion.section
                key="r-step2"
                initial="hidden"
                animate="visible"
                exit="exit"
                variants={stepVariants}
                transition={{ duration: 0.5 }}
                className="space-y-6"
              >
                <h2 className="text-2xl font-semibold">Company</h2>
                <div className="relative">
                  <label className="block text-base text-gray-700 mb-2">Search company</label>
                  <div className="relative">
                    <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35m1.1-5.4a6.75 6.75 0 11-13.5 0 6.75 6.75 0 011.5 0 6.75 6.75 0 0012 0z" />
                      </svg>
                    </span>
                    <input
                      className="w-full pl-11 pr-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 shadow-sm"
                      placeholder="Type at least 2 characters to search"
                      value={companyQuery}
                      onChange={(e)=>setCompanyQuery(e.target.value)}
                    />
                  </div>
                  {debouncedQuery.length >= 2 && companyResults?.length > 0 && !selectedCompany && (
                    <div className="absolute z-20 mt-2 w-full bg-white border border-gray-200 rounded-xl shadow-lg max-h-64 overflow-auto">
                      {companyResults.map((c) => (
                        <button
                          key={c.id}
                          type="button"
                          onClick={()=>{ setSelectedCompany({ id: c.id, company_name: c.company_name }); setCompanyQuery(c.company_name); }}
                          className={`w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 ${selectedCompany?.id===c.id? 'bg-indigo-50' : ''}`}
                        >
                          <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-indigo-100 text-indigo-700 text-sm font-medium">
                            {c.company_name?.[0]?.toUpperCase() || 'C'}
                          </span>
                          <span className="text-gray-800">{c.company_name}</span>
                        </button>
                      ))}
                    </div>
                  )}
                  {debouncedQuery.length >= 2 && companyResults?.length === 0 && (
                    <div className="mt-2 text-sm text-gray-500">No matching companies.</div>
                  )}
                </div>
                {selectedCompany && (
                  <div className="flex items-center gap-2 text-sm">
                    <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200">
                      <span className="font-medium">{selectedCompany.company_name}</span>
                    </span>
                    <button
                      type="button"
                      className="text-gray-500 hover:text-gray-700 underline"
                      onClick={()=>{ setSelectedCompany(null); }}
                      aria-label="Clear selected company"
                    >
                      Clear
                    </button>
                  </div>
                )}
                {/* No new company creation in recruiter flow */}

                {/* No company detail creation fields in recruiter flow */}

                {/* Cluster / Unit selection (auto-detected per company) */}
                {selectedCompany && (
                  <div className="space-y-4">
                    <h3 className="text-xl font-semibold">Structure</h3>
                    {/* Helper text for current company's structure */}
                    <p className="text-sm text-gray-600">
                      {hasCluster && hasUnit && 'This company has clusters and units. You may select a cluster and/or a unit. You can finish with company only too.'}
                      {!hasCluster && hasUnit && 'This company has units but no clusters. You may select a unit or finish with company only.'}
                      {hasCluster && !hasUnit && 'This company has clusters but no units. You may select a cluster or finish with company only.'}
                      {!hasCluster && !hasUnit && 'This company has no clusters or units. Selecting the company is sufficient.'}
                    </p>

                    {hasCluster && (
                      <div className="space-y-2">
                        <label className="block text-base text-gray-700">Select cluster</label>
                        <select
                          className="w-full border rounded-lg p-3"
                          value={selectedCluster?.id || ''}
                          onChange={(e)=>{
                            const id = e.target.value ? Number(e.target.value) : null;
                            const cl = clusterResults.find(c=>String(c.id)===String(id));
                            setSelectedCluster(cl ? { id: cl.id, name: cl.name } : null);
                          }}
                        >
                          <option value="">Select a cluster</option>
                          {clusterResults.map(cl => (
                            <option key={cl.id} value={cl.id}>{cl.name}</option>
                          ))}
                        </select>
                      </div>
                    )}

                    {hasUnit && (
                      <div className="space-y-2">
                        <label className="block text-base text-gray-700">Select unit {hasCluster && selectedCluster ? `(under ${selectedCluster.name})` : (hasCluster ? '(select a cluster first)' : '(optional)')}</label>
                        {(hasCluster && !selectedCluster) ? (
                          <div className="text-sm text-gray-500">Select a cluster first to view units.</div>
                        ) : (
                          <select
                            className="w-full border rounded-lg p-3"
                            value={selectedUnit?.id || ''}
                            onChange={(e)=>{
                              const id = e.target.value ? Number(e.target.value) : null;
                              const u = unitResults.find(x=>String(x.id)===String(id));
                              setSelectedUnit(u ? { id: u.id, name: u.name } : null);
                            }}
                          >
                            <option value="">Select a unit</option>
                            {unitResults.map(u => (
                              <option key={u.id} value={u.id}>{u.name}</option>
                            ))}
                          </select>
                        )}
                      </div>
                    )}
                  </div>
                )}

                <div className="flex justify-between mt-6">
                  <button
                    className="px-6 py-3 rounded-lg border text-lg font-medium hover:bg-gray-100 transition"
                    onClick={()=>setStep(1)}
                  >
                    Back
                  </button>
                  <button
                    disabled={saving || !canFinish}
                    className="px-6 py-3 rounded-lg bg-indigo-600 text-white text-lg font-medium hover:bg-indigo-700 transition disabled:opacity-50"
                    onClick={async()=>{ await handleSave(); }}
                  >
                    {saving ? 'Saving...' : 'Finish'}
                  </button>
                </div>
              </motion.section>
            )}
          </AnimatePresence>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default RecruiterOnbp;
